import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import axios from 'axios';
import WorkflowNavigation from './WorkflowNavigation';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const [data, setData] = useState({
    wines: [],
    workorders: [],
    inventory: {
      lowStock: [],
      totalProducts: 0
    },
    stats: {
      totalGallonsRed: 0,
      totalGallonsWhite: 0,
      totalGallonsInTank: 0,
      totalGallonsInBarrel: 0
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [winesRes, workordersRes, inventoryRes] = await Promise.all([
          axios.get('/api/wines?active=1&limit=100'),
          axios.get('/api/workorders?status=Open&limit=5'),
          axios.get('/api/products')
        ]);

        // Process wines data by varietal
        const winesByVarietal = winesRes.data.wines.reduce((acc, wine) => {
          if (wine.varietal && wine.totalGallons) {
            acc[wine.varietal] = (acc[wine.varietal] || 0) + (wine.totalGallons || 0);
          }
          return acc;
        }, {});

        // Sort varietals by volume and take top 8
        const sortedVarietals = Object.entries(winesByVarietal)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 8);
        
        // Group remaining varietals as 'Others'
        const otherVarietals = Object.entries(winesByVarietal)
          .sort(([,a], [,b]) => b - a)
          .slice(8)
          .reduce((sum, [,value]) => sum + value, 0);

        if (otherVarietals > 0) {
          sortedVarietals.push(['Others', otherVarietals]);
        }

        // Calculate stats
        const stats = winesRes.data.wines.reduce((acc, wine) => {
          if (wine.varietalDetail === 'Red') acc.totalGallonsRed += wine.totalGallons || 0;
          if (wine.varietalDetail === 'White') acc.totalGallonsWhite += wine.totalGallons || 0;
          return acc;
        }, { totalGallonsRed: 0, totalGallonsWhite: 0 });

        setData({
          wines: Object.entries(winesByVarietal).map(([name, value]) => ({ name, value })),
          workorders: workordersRes.data || [],
          inventory: {
            lowStock: inventoryRes.data.products.filter(p => p.inventory < 10) || [],
            totalProducts: inventoryRes.data.products.length
          },
          stats
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const pieChartData = {
    labels: data.wines.map(item => item.name),
    datasets: [
      {
        data: data.wines.map(item => item.value),
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom'
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${Math.round(context.raw)} gallons`
        }
      }
    },
    maintainAspectRatio: false
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header with Welcome Message and Logout */}
      <div className="card mb-4">
        <div className="card-body d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Dashboard</h4>
          <div className="d-flex align-items-center">
            <span className="me-3">
              Welcome, {user?.firstName} {user?.lastName}
            </span>
            <button
              onClick={handleLogout}
              className="btn btn-outline-primary"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Add the Workflow Navigation Component */}
      <WorkflowNavigation onNavigate={handleNavigate} />

      {/* Quick Stats Row */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Red Wine</h6>
              <h2 className="card-title">{Math.round(data.stats.totalGallonsRed)} gal</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total White Wine</h6>
              <h2 className="card-title">{Math.round(data.stats.totalGallonsWhite)} gal</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Open Work Orders</h6>
              <h2 className="card-title">{data.workorders.length}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Low Stock Products</h6>
              <h2 className="card-title">{data.inventory.lowStock.length}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Wine Distribution by Varietal</h5>
            </div>
            <div className="card-body" style={{ height: '400px', position: 'relative' }}>
              <Pie data={pieChartData} options={pieChartOptions} />
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Recent Work Orders</h5>
            </div>
            <div className="card-body">
              <div className="list-group">
                {data.workorders.slice(0, 5).map((order) => (
                  <div 
                    key={order.id} 
                    className="list-group-item list-group-item-action"
                    onClick={() => navigate(`/workorders/${order.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <h6 className="mb-1">{order.wineLot} - Lot {order.wineID}</h6>
                      <span className="badge bg-warning">
                        {order.status}
                      </span>
                    </div>
                    <small className="text-muted">
                      Due: {new Date(order.date).toLocaleDateString()} | 
                      Assigned to: {order.assignedTo || 'Unassigned'}
                    </small>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Low Stock Alert Section */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Low Stock Alerts</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Vintage</th>
                      <th>Varietal</th>
                      <th>Current Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.inventory.lowStock.slice(0, 5).map((product) => {
                      const cases = Math.floor(product.inventory / 12);
                      return (
                        <tr 
                          key={product.productID}
                          onClick={() => navigate(`/products/${product.productID}`)}
                          style={{ cursor: 'pointer' }}
                          className="table-row-hover"
                        >
                          <td>{product.proFanciful}</td>
                          <td>{product.proVintage}</td>
                          <td>{product.proVarietal}</td>
                          <td>
                            <span className="badge bg-danger">
                              {cases} cases remaining
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;